import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MailrailBaseEmailCampaign, TEmailCampaignMailing } from '@feathr/blackbox';
import { CardV2 as Card } from '@feathr/components';
import type { ICampaignValidationErrors } from '@feathr/extender/App/EventsPage/CampaignSummary';
import TemplateMergefieldDefaults from '@feathr/extender/App/TemplatePage/TemplateMetadataForm/TemplateMergefieldDefaults';
import { useStore } from '@feathr/extender/state';

import EmailTemplateConfig from '../../EmailTemplateConfig';
import SenderInformationConfig from '../../SenderInformationConfig';

export interface IMailrailMailingConfigProps {
  campaign: MailrailBaseEmailCampaign;
  children?: ReactNode;
  disabled: boolean;
  mailing: TEmailCampaignMailing;
  validationErrors: ICampaignValidationErrors;
  variant?: 'cards' | 'combined';
}

function MailrailMailingConfig({
  campaign,
  children,
  disabled,
  mailing,
  validationErrors,
  variant = 'cards',
}: Readonly<IMailrailMailingConfigProps>): JSX.Element {
  const { t } = useTranslation();
  const { Templates } = useStore();

  const templateId = mailing.get('template_id');
  const template = templateId ? Templates.get(templateId) : undefined;

  const senderInformation = (
    <SenderInformationConfig
      disabled={disabled}
      mailing={mailing}
      validationErrors={validationErrors}
      variant={variant === 'combined' ? 'fields' : 'card'}
    />
  );

  return (
    <>
      {variant === 'combined' ? (
        <Card>
          <Card.Header title={t('Email Information')} />
          <Card.Content addVerticalGap={true}>
            {children}
            <EmailTemplateConfig
              campaign={campaign}
              disabled={disabled}
              mailing={mailing}
              template={template}
              validationErrors={validationErrors}
              variant={'fields'}
            >
              {senderInformation}
            </EmailTemplateConfig>
          </Card.Content>
        </Card>
      ) : (
        <>
          {senderInformation}
          <EmailTemplateConfig
            campaign={campaign}
            disabled={disabled}
            mailing={mailing}
            template={template}
            validationErrors={validationErrors}
          />
        </>
      )}
      {template && !!template.get('used_mergefields')?.length && (
        <TemplateMergefieldDefaults
          disabled={disabled || template.isReadOnly}
          template={template}
        />
      )}
    </>
  );
}

export default observer(MailrailMailingConfig);
